import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import { Library, BookOpen, Search, FileText, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import toast, { Toaster } from 'react-hot-toast';
import { getToken } from "../services/auth";
import { jwtDecode } from "jwt-decode";

export default function BooksPage() {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState("student");
  const navigate = useNavigate();

  useEffect(() => {
    const token = getToken();
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setRole(decoded.role || "student");
      } catch { }
    }

    api.get("/books")
      .then(res => setBooks(res.data || []))
      .catch(() => toast.error("Failed to load books library"))
      .finally(() => setLoading(false));
  }, []);

  const Skeleton = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
      {[1, 2, 3, 4].map(i => (
        <div key={i} className="bg-white dark:bg-slate-900 rounded-3xl p-4 shadow-sm animate-pulse">
          <div className="aspect-[3/4] bg-slate-100 dark:bg-slate-800 rounded-2xl mb-4" />
          <div className="h-4 w-3/4 bg-slate-100 dark:bg-slate-800 rounded mb-2" />
          <div className="h-3 w-1/2 bg-slate-100 dark:bg-slate-800 rounded" />
        </div>
      ))}
    </div>
  );

  return (
    <div className="w-full">
      <Toaster position="top-center" />
      
      <main className="flex-1 p-4 sm:p-6 lg:p-10 min-w-0">
        <div className="mb-8 flex flex-col justify-between gap-5 md:flex-row md:items-center lg:mb-10">
          <div>
            <h1 className="page-title flex items-center gap-3">
              <Library className="text-indigo-600" /> Recommended Books
            </h1>
            <p className="text-slate-500 mt-1 text-sm font-medium">Curated digital library to boost your preparation</p>
          </div>

          <div className="relative group">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-600 transition-colors" />
            <input
              type="text"
              placeholder="Search library..."
              className="form-control pl-11 md:w-72"
            />
          </div>
        </div>

        {loading ? (
          <Skeleton />
        ) : books.length === 0 ? (
          <div className="surface p-10 text-center sm:p-16">
            <Library size={48} className="mx-auto text-slate-300 mb-4" />
            <h3 className="text-xl font-black dark:text-white">No books available yet</h3>
            <p className="text-slate-500 mt-2 text-sm font-medium">Check back later as instructors upload more study material.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-6">
            {books.map((book) => (
              <motion.div
                key={book.id}
                whileHover={{ y: -6 }}
                className="group flex flex-col rounded-lg border border-slate-200 bg-white p-4 shadow-sm transition-all hover:border-indigo-200 hover:shadow-xl hover:shadow-indigo-600/10 dark:border-slate-800 dark:bg-slate-900"
              >
                <div className="aspect-[3/4] rounded-2xl overflow-hidden bg-indigo-50 dark:bg-slate-800 mb-4 shadow-inner relative">
                  {book.thumbnail ? (
                    <img src={book.thumbnail} alt="" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <BookOpen size={32} className="text-indigo-200" />
                    </div>
                  )}
                  <div className="absolute bottom-2 right-2 bg-black/60 backdrop-blur-md text-white text-[10px] px-2 py-1 rounded-lg font-bold tracking-wide flex items-center gap-1">
                    <FileText size={10} /> PDF
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <p className="text-[9px] font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-widest mb-1 line-clamp-1">{book.subject || "General"}</p>
                  <h3 className="font-bold text-slate-900 dark:text-white leading-tight line-clamp-2 mb-1">{book.title}</h3>
                  <p className="text-xs text-slate-400 font-medium line-clamp-1 mb-3">By {book.author || "Unknown Author"}</p>
                </div>

                <button
                  onClick={() => navigate(`/read-book/${book.id}`)}
                  className="w-full py-3 bg-slate-50 dark:bg-slate-800 hover:bg-indigo-600 hover:text-white text-indigo-600 dark:text-indigo-400 font-black text-xs uppercase tracking-widest rounded-xl transition-all flex items-center justify-center gap-2"
                >
                  Start Reading <ArrowRight size={14} />
                </button>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
